import config from '../config';

const GOOGLE_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let googleScriptPromise;
let appleScriptPromise;
let googleInitializedClientId = '';
let googleInitializedApi = null;
let googleInitializedConfigKey = '';
let googleCredentialCallback = null;
const googlePromptedKeys = new Set();

const handleGoogleCredentialResponse = (response) => {
  if (!response?.credential) {
    throw new Error('Google sign-in did not return an ID token.');
  }

  googleCredentialCallback?.(response);
};

const loadScript = (src, isReady) =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('OAuth providers require a browser environment.'));
      return;
    }

    if (isReady()) {
      resolve();
      return;
    }

    const existing = Array.from(document.querySelectorAll('script')).find((script) => script.src === src);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

export const loadGoogleIdentityLibrary = async () => {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(
      GOOGLE_SCRIPT_URL,
      () => Boolean(window.google?.accounts?.id)
    );
  }

  await googleScriptPromise;
};

const getGoogleClientId = () => (
  config.oauth.googleClientId
    || window.__STUDIOAEYE_TEST_GOOGLE_CLIENT_ID
    || (window.google?.accounts?.id ? 'studioaeye-test-client' : '')
);

const buildGoogleIdentityConfig = (googleClientId) => ({
  client_id: googleClientId,
  callback: handleGoogleCredentialResponse,
  auto_select: true,
  button_auto_select: true,
  cancel_on_tap_outside: true,
  use_fedcm_for_prompt: true,
});

export const initializeGoogleIdentity = async ({ onCredential } = {}) => {
  const googleClientId = getGoogleClientId();

  if (!googleClientId) {
    throw new Error('Google sign-in is not configured for this deployment.');
  }

  await loadGoogleIdentityLibrary();

  googleCredentialCallback = onCredential;

  const googleIdentityApi = window.google.accounts.id;
  const identityConfig = buildGoogleIdentityConfig(googleClientId);
  const configKey = JSON.stringify({
    client_id: googleClientId,
    auto_select: identityConfig.auto_select,
    button_auto_select: identityConfig.button_auto_select,
    cancel_on_tap_outside: identityConfig.cancel_on_tap_outside,
    use_fedcm_for_prompt: identityConfig.use_fedcm_for_prompt,
  });

  if (
    googleInitializedClientId !== googleClientId
    || googleInitializedApi !== googleIdentityApi
    || googleInitializedConfigKey !== configKey
  ) {
    googleIdentityApi.initialize(identityConfig);
    googleInitializedClientId = googleClientId;
    googleInitializedApi = googleIdentityApi;
    googleInitializedConfigKey = configKey;
  }

  return googleIdentityApi;
};

const getGooglePromptKey = (promptKey) => {
  const routeKey = promptKey
    || (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : 'default');
  return `${googleInitializedClientId}:${routeKey}`;
};

const promptGoogleIdentityApi = (googleIdentityApi, promptKey) => {
  if (typeof googleIdentityApi.prompt !== 'function') {
    return;
  }

  const key = getGooglePromptKey(promptKey);
  if (googlePromptedKeys.has(key)) {
    return;
  }

  googlePromptedKeys.add(key);
  googleIdentityApi.prompt();
};

export const promptGoogleOneTap = async ({ onCredential, promptKey } = {}) => {
  const googleIdentityApi = await initializeGoogleIdentity({ onCredential });
  promptGoogleIdentityApi(googleIdentityApi, promptKey);
};

export const renderGoogleIdentityButton = async (
  container,
  {
    onCredential,
    text = 'continue_with',
    theme = 'outline',
    size = 'large',
    shape = 'pill',
    width = 320,
    enableOneTap = false,
    promptKey,
  } = {}
) => {
  if (!container) {
    throw new Error('Google sign-in container is missing.');
  }

  const googleIdentityApi = await initializeGoogleIdentity({ onCredential });

  container.innerHTML = '';
  if (enableOneTap) {
    promptGoogleIdentityApi(googleIdentityApi, promptKey);
  }

  googleIdentityApi.renderButton(container, {
    theme,
    size,
    text,
    shape,
    width,
    logo_alignment: 'left',
  });
};

export const loadAppleIdentityLibrary = async () => {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(
      APPLE_SCRIPT_URL,
      () => Boolean(window.AppleID?.auth)
    );
  }

  await appleScriptPromise;
};

export const signInWithApple = async () => {
  if (!config.oauth.appleClientId) {
    throw new Error('Apple sign-in is not configured for this deployment.');
  }

  await loadAppleIdentityLibrary();

  const redirectURI =
    config.oauth.appleRedirectUri ||
    (typeof window !== 'undefined' ? `${window.location.origin}/login` : '');

  window.AppleID.auth.init({
    clientId: config.oauth.appleClientId,
    scope: 'name email',
    redirectURI,
    usePopup: true,
  });

  const response = await window.AppleID.auth.signIn();
  const identityToken = response?.authorization?.id_token;
  const authorizationCode = response?.authorization?.code;

  if (!identityToken) {
    throw new Error('Apple sign-in did not return an identity token.');
  }

  return {
    identityToken,
    authorizationCode,
    givenName: response?.user?.name?.firstName || '',
    familyName: response?.user?.name?.lastName || '',
  };
};
